import React, { useState, useEffect } from 'react';
import { ImageWithFallback } from './components/figma/ImageWithFallback';
import { PawPrint, Instagram, Facebook, Globe, MapPin, Phone, Map, Navigation, MessageCircle, Clock } from 'lucide-react';
import { ImageCarousel } from './components/ImageCarousel';
import { AdminPanel } from './components/AdminPanel';
import { AdminSetup } from './components/AdminSetup';
import { BookingForm } from './components/BookingForm';
import { Header } from './components/Header';
import { SEO, LocalBusinessSchema, OrganizationSchema, FAQSchema, BreadcrumbSchema } from './components/SEO';
import { GoogleAnalytics, GoogleSearchConsole, GoogleTagManager } from './components/GoogleAnalytics';
import { StickyCallBar, FloatingCallButton } from './components/StickyCallBar';
import { LandingPage } from './components/LandingPage';
import { CookieConsent } from './components/CookieConsent';
import { API_ENDPOINTS, isBackendAvailable, setUsingFallback } from './utils/api';
import { dbImages } from './utils/dbBackend';
import logoFull from 'figma:asset/fce628fa1ff2c0ce8e1c3b074b0a7732c9200683.png';
import logoSmall from 'figma:asset/cae93287f41b9869e100c8c0d54758aa4243338c.png';

type Language = 'he' | 'en' | 'ru';

interface Translations {
  hero: {
    main: string;
    subHeadline: string;
    subtitle: string;
  };
  services: {
    title: string;
    items: string[];
  };
  cta: string;
  contact: {
    phone: string;
    address: string;
    addressText: string;
    navigation: string;
    social: string;
    whatsapp: string;
  };
}

const translations: Record<Language, Translations> = {
  he: {
    hero: {
      main: 'חדש בזכרון יעקב!',
      subHeadline: 'סלון יופי לכלבים',
      subtitle: 'במרכז זכרון יעקב, עם טיפוח מקצועי ועדין לכלב שלכם.',
    },
    services: {
      title: 'טיפוח מקצועי ועדין',
      items: ['היגיינה', 'חפיפה', 'תספורת'],
    },
    cta: 'התקשרו לקביעת תור',
    contact: {
      phone: 'טלפון:',
      address: 'כתובת:',
      addressText: 'רח\' המייסדים 51, זכרון יעקב',
      navigation: 'ניווט:',
      social: 'עקבו אחרינו:',
      whatsapp: 'WhatsApp:',
    },
  },
  en: {
    hero: {
      main: 'New in Zichron Ya\'akov!',
      subHeadline: 'Beauty salon for dogs',
      subtitle: 'In the heart of Zichron Ya\'akov, offering gentle, professional care for your dog.',
    },
    services: {
      title: 'Professional & gentle grooming',
      items: ['Hygiene', 'Bath & blow-dry', 'Haircut'],
    },
    cta: 'Call to book an appointment',
    contact: {
      phone: 'Phone:',
      address: 'Address:',
      addressText: '51 HaMeyasdim St, Zichron Ya\'akov',
      navigation: 'Navigation:',
      social: 'Follow us:',
      whatsapp: 'WhatsApp:',
    },
  },
  ru: {
    hero: {
      main: 'Новинка в Зихрон-Яакове!',
      subHeadline: 'Салон красоты для собак',
      subtitle: 'В самом центре Зихрон-Яакова – нежный и профессиональный уход для вашей собаки.',
    },
    services: {
      title: 'Профессиональный и бережный уход',
      items: ['Гигиена', 'Купание', 'Стрижка'],
    },
    cta: 'Позвоните, чтобы записаться',
    contact: {
      phone: 'Телефон:',
      address: 'Адрес:',
      addressText: 'ул. hа-Меясдим 51, Зихрон-Яаков',
      navigation: 'Навигация:',
      social: 'Подписывайтесь на нас:',
      whatsapp: 'WhatsApp:',
    },
  },
};

interface GalleryImage {
  id: string;
  imageUrl: string;
  uploadedAt: string;
  seo?: {
    primary_alt_he?: string;
    primary_alt_en?: string;
    title_he?: string;
    figcaption_he?: string;
    keywords_he?: string[];
    breed_detected?: string;
    json_ld_imageobject?: string;
  };
}

export default function App() {
  const [language, setLanguage] = useState<Language>('he');
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [showAdminSetup, setShowAdminSetup] = useState(false);
  const t = translations[language];
  const isRTL = language === 'he';

  useEffect(() => {
    // Handle navigation
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
    };
    window.addEventListener('popstate', handlePopState);
    
    // Check if we should show admin setup
    const params = new URLSearchParams(window.location.search);
    if (params.get('setup') === 'true') {
      setShowAdminSetup(true);
    }
    
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    // Fetch images for the gallery
    fetchImages();
  }, []);

  const fetchImages = async () => {
    let allImages: GalleryImage[] = [];

    // Helper to map any image format to GalleryImage
    const mapImage = (img: any): GalleryImage => ({
      id: img.id,
      imageUrl: img.imageUrl || img.afterUrl || img.beforeUrl || '',
      uploadedAt: img.uploadedAt,
      seo: img.seo ? {
        primary_alt_he: img.seo.primary_alt_he,
        primary_alt_en: img.seo.primary_alt_en,
        title_he: img.seo.title_he,
        figcaption_he: img.seo.figcaption_he,
        keywords_he: img.seo.keywords_he,
        breed_detected: img.seo.breed_detected,
        json_ld_imageobject: img.seo.json_ld_imageobject,
      } : undefined,
    });

    try {
      // First, try to load static images from gallery.json
      try {
        const staticResponse = await fetch('/images/gallery.json');
        if (staticResponse.ok) {
          const staticData = await staticResponse.json();
          if (staticData.images && staticData.images.length > 0) {
            allImages = [...staticData.images.map(mapImage)];
          }
        }
      } catch (e) {
        // No static gallery found
      }

      // Check if backend is available
      const backendAvailable = await isBackendAvailable();

      if (!backendAvailable) {
        // Use IndexedDB (Dexie) fallback
        setUsingFallback(true);
        const localData = await dbImages.getAll();
        // Convert Blobs to persistent data URLs
        const localImages: GalleryImage[] = await Promise.all(
          localData.map(async (img) => ({
            id: img.id,
            imageUrl: await dbImages.getImageDataUrl(img) || '',
            uploadedAt: img.uploadedAt,
            seo: img.seo ? {
              primary_alt_he: img.seo.primary_alt_he,
              primary_alt_en: img.seo.primary_alt_en,
              title_he: img.seo.title_he,
              figcaption_he: img.seo.figcaption_he,
              keywords_he: img.seo.keywords_he,
              breed_detected: img.seo.breed_detected,
              json_ld_imageobject: img.seo.json_ld_imageobject,
            } : undefined,
          }))
        );
        allImages = [...allImages, ...localImages];
        setImages(allImages);
        return;
      }

      // Try to fetch from backend
      const response = await fetch(API_ENDPOINTS.images);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      if (data.images) {
        allImages = [...allImages, ...data.images.map(mapImage)];
      }
      setImages(allImages);
    } catch (error) {
      // Fallback to IndexedDB
      setUsingFallback(true);
      const localData = await dbImages.getAll();
      // Convert Blobs to persistent data URLs
      const localImages: GalleryImage[] = await Promise.all(
        localData.map(async (img) => ({
          id: img.id,
          imageUrl: await dbImages.getImageDataUrl(img) || '',
          uploadedAt: img.uploadedAt,
          seo: img.seo ? {
            primary_alt_he: img.seo.primary_alt_he,
            primary_alt_en: img.seo.primary_alt_en,
            title_he: img.seo.title_he,
            figcaption_he: img.seo.figcaption_he,
            keywords_he: img.seo.keywords_he,
            breed_detected: img.seo.breed_detected,
            json_ld_imageobject: img.seo.json_ld_imageobject,
          } : undefined,
        }))
      );
      allImages = [...allImages, ...localImages];
      setImages(allImages);
    }
  };

  // Show admin setup if requested
  if (showAdminSetup) {
    return <AdminSetup />;
  }

  // Show landing page if on /land1 route
  if (currentPath === '/land1' || currentPath.startsWith('/land1')) {
    return <LandingPage />;
  }

  // Show admin panel if on /admin route
  if (currentPath === '/admin' || currentPath.startsWith('/admin')) {
    return <AdminPanel />;
  }

  return (
  <div
    dir={isRTL ? 'rtl' : 'ltr'}
    className="min-h-screen"
    style={{ backgroundColor: '#F0C2B5' }}
  >
    {/* SEO Components */}
    <SEO
      title="מספרת כלבים בזכרון יעקב — ShowGroom"
      description="ShowGroom — מספרת כלבים וחתולים בזכרון יעקב. אמבטיה, תספורות, גזירת ציפורניים וטיפולים היפואלרגניים. קבעו תור: 077-2200052"
      canonical="https://showgroom.co.il/"
      ogImage="https://showgroom.co.il/images/showgroom-og.png"
      ogImageAlt="ShowGroom - מספרת כלבים וחתולים בזכרון־יעקב"
    />
      <LocalBusinessSchema />
      <OrganizationSchema />
      <FAQSchema />
      <BreadcrumbSchema />
      
      {/* Google Analytics - GA4 */}
      <GoogleAnalytics measurementId="G-2WY16JV3LW" />
      
      {/* Google Search Console - Replace with your actual verification code */}
      <GoogleSearchConsole verificationCode="GSC_META_TAG_CONTENT_HERE" />

      {/* Google Tag Manager */}
      <GoogleTagManager containerId="GTM-NRSLWWMN" />

      {/* Header with Navigation */}
      <Header language={language} onLanguageChange={setLanguage} />

      {/* Main Content with padding for fixed header */}
      <main className="pt-20 flex items-center justify-center p-4 sm:p-6 md:p-8">
        <div className="w-full max-w-[960px]">
          {/* Main Card */}
          <div
            id="home"
            className="rounded-3xl shadow-lg p-6 sm:p-8 md:p-12 scroll-mt-24"
            style={{ backgroundColor: '#F4EED7' }}
          >
            {/* Hero Section */}
            <div className="flex flex-col items-center text-center gap-6 mb-12">
              {/* Logo - 2x bigger */}
              <div className="w-64 h-64 md:w-80 md:h-80">
                <ImageWithFallback
                  src={logoFull}
                  alt="ShowGroom Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              
              {/* ShowGroom brand name under logo */}
              <h1
                className="text-5xl md:text-6xl lg:text-7xl -mt-20"
                style={{
                  color: '#444444',
                  fontFamily: "'Lexend', sans-serif",
                  fontWeight: '600',
                }}
              >
                ShowGroom
              </h1>
              
              {/* Sub-headline - salon description (directly under ShowGroom) */}
              <h2
                className="text-2xl md:text-3xl lg:text-4xl px-4 -mt-2"
                style={{
                  color: '#444444',
                  fontFamily: "'Arimo', sans-serif",
                  fontWeight: '500',
                  lineHeight: '1.3'
                }}
              >
                {t.hero.subHeadline}
              </h2>
              
              {/* Sub-headline */}
              <p
                className="text-xl md:text-2xl font-semibold"
                style={{ color: '#444444' }}
              >
                {t.hero.main}
              </p>
              
              {/* Subtitle - improved color contrast (#444444 instead of #444444) */}
              <p
                className="max-w-2xl px-4 text-lg"
                style={{ color: '#444444' }}
              >
                {t.hero.subtitle}
              </p>
            </div>

            {/* Services Section */}
            <section
              id="services"
              className={`mb-10 p-6 rounded-2xl scroll-mt-24 ${isRTL ? 'text-right' : 'text-left'}`}
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.5)' }}
              aria-labelledby="services-heading"
            >
              <h2
                id="services-heading"
                className="mb-6 text-xl font-semibold"
                style={{ color: '#444444' }}
              >
                {t.services.title}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {t.services.items.map((service, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-4 rounded-xl"
                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.6)' }}
                  >
                    <PawPrint size={24} style={{ color: '#F29B70' }} />
                    <span style={{ color: '#444444' }}>{service}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* CTA Button */}
            <div className="text-center mb-12">
              <a
                href="tel:+972772200052"
                className="inline-block px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                style={{
                  backgroundColor: '#F29B70',
                  color: '#444444',
                }}
              >
                {t.cta}
              </a>
            </div>

            {/* Contact Section */}
            <div
              id="contact"
              className={`p-6 rounded-2xl scroll-mt-24 ${isRTL ? 'text-right' : 'text-left'}`}
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.5)' }}
            >
              {/* Phone */}
              <div className="mb-4 flex items-center gap-3">
                <Phone size={20} style={{ color: '#F29B70' }} />
                <span style={{ color: '#444444' }} className="inline-block min-w-[80px]">
                  {t.contact.phone}
                </span>
                <a
                  href="tel:+972772200052"
                  className="hover:underline"
                  style={{ color: '#444444' }}
                >
                  077-2200052
                </a>
              </div>

              {/* WhatsApp - with pre-filled message */}
              <div className="mb-4 flex items-center gap-3">
                <MessageCircle size={20} style={{ color: '#F29B70' }} />
                <span style={{ color: '#444444' }} className="inline-block min-w-[80px]">
                  {t.contact.whatsapp}
                </span>
                <a
                  href="https://wa.me/972555171717?text=%D7%94%D7%99%D7%99!%20%D7%90%D7%A0%D7%99%20%D7%9E%D7%A2%D7%95%D7%A0%D7%99%D7%99%D7%9F%2F%D7%AA%20%D7%9C%D7%A7%D7%91%D7%95%D7%A2%20%D7%AA%D7%95%D7%A8%20%D7%9C%D7%98%D7%99%D7%A4%D7%95%D7%97.%20%F0%9F%90%95"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                  style={{ color: '#444444' }}
                >
                  055-5171717
                </a>
              </div>

              {/* Address */}
              <div className="mb-4 flex items-center gap-3">
                <MapPin size={20} style={{ color: '#F29B70' }} />
                <span style={{ color: '#444444' }} className="inline-block min-w-[80px]">
                  {t.contact.address}
                </span>
                <a
                  href="https://maps.app.goo.gl/TXPisbXuR6dsEFhaA"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                  style={{ color: '#444444' }}
                >
                  {t.contact.addressText}
                </a>
              </div>

              {/* Navigation */}
              <div className="mb-4">
                <div style={{ color: '#444444' }} className="mb-3">
                  {t.contact.navigation}
                </div>
                <div className="flex gap-3">
                  <a
                    href="https://maps.app.goo.gl/TXPisbXuR6dsEFhaA"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 rounded-lg hover:shadow-md transition-all flex items-center gap-2"
                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', color: '#444444' }}
                  >
                    <Map size={18} style={{ color: '#F29B70' }} />
                    <span>Google Maps</span>
                  </a>
                  <a
                    href="https://waze.com/ul/hsvbc73u51"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 rounded-lg hover:shadow-md transition-all flex items-center gap-2"
                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', color: '#444444' }}
                  >
                    <Navigation size={18} style={{ color: '#F29B70' }} />
                    <span>Waze</span>
                  </a>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <div style={{ color: '#444444' }} className="mb-2">
                  {t.contact.social}
                </div>
                <div className="flex flex-col gap-2">
                  <a
                    href="https://www.instagram.com/showgroom_25"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline flex items-center gap-2"
                    style={{ color: '#444444' }}
                  >
                    <Instagram size={20} style={{ color: '#F29B70' }} />
                    <span>Instagram</span>
                  </a>
                  <a
                    href="https://www.facebook.com/showgroom.2025"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline flex items-center gap-2"
                    style={{ color: '#444444' }}
                  >
                    <Facebook size={20} style={{ color: '#F29B70' }} />
                    <span>Facebook</span>
                  </a>
                  <a
                    href="https://www.showgroom.co.il"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline flex items-center gap-2"
                    style={{ color: '#444444' }}
                  >
                    <Globe size={20} style={{ color: '#F29B70' }} />
                    <span>www.showgroom.co.il</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Gallery */}
            <div id="gallery" className="mb-12 scroll-mt-24">{images.length > 0 ? (
                <ImageCarousel images={images} language={language} />
              ) : (
                <div
                  className="p-6 rounded-2xl text-center"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.5)', color: '#444444' }}
                >
                  <p className="mb-2">Gallery is ready!</p>
                  <p>Upload images from the Admin Panel to display them here.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Sticky Call/WhatsApp Bar (Mobile) */}
      <StickyCallBar language={language} />
      
      {/* Floating Call/WhatsApp Buttons (Desktop) */}
      <FloatingCallButton language={language} />

      {/* Cookie Consent Banner */}
      <CookieConsent language={language} />
    </div>
  );
}